import type { FC } from 'react';
import React, { useMemo, useRef } from 'react';
import YouTube from 'react-youtube';
import type { YouTubeEvent, YouTubePlayer } from 'react-youtube';
import { Box } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { useDocumentVisibility } from '@shared/utils/hooks/useDocumentVisibility';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { addError } from '@shared/utils/logger';
import type { YoutubeVideoContainerProps } from './YoutubeVideoContainer.types';
import { useTimeout } from '@shared/hooks/useTimeout';

const PAUSED_RETRY_MAX = 3;
const GENERAL_RETRY_MAX = 10;
const RETRY_DELAY_MS = 1000;

const YOUTUBE_ERROR_CODES: Record<number, string> = {
  2: 'invalid_parameter',
  5: 'html5_player_error',
  100: 'video_not_found',
  101: 'embed_not_allowed',
  150: 'embed_not_allowed',
};

export const YoutubeVideoContainer: FC<YoutubeVideoContainerProps> = ({ videoId, videoUrl, showVideo, onReady, onError, minWidth, minHeight }) => {
  const { tq } = useTranslation('videoBackground');
  const playerRef = useRef<YouTubePlayer | null>(null);
  const pausedRetryCountRef = useRef(0);
  const generalRetryCountRef = useRef(0);
  const retryTimeout = useTimeout();

  const forcePlay = useEventCallback(() => {
    const player = playerRef.current;
    if (!player) return;
    player.mute();
    player.playVideo();
  });

  useDocumentVisibility({
    onVisible: () => {
      forcePlay();
    },
  });

  const handleReady = useEventCallback((event: YouTubeEvent) => {
    playerRef.current = event.target;
    forcePlay();
    onReady();
  });

  const handleStateChange = useEventCallback((event: YouTubeEvent<number>) => {
    const state = event.data;

    // Reset retry counters and cancel pending retry whenever playback succeeds
    if (state === YouTube.PlayerState.PLAYING) {
      retryTimeout.clear();

      pausedRetryCountRef.current = 0;
      generalRetryCountRef.current = 0;
      return;
    }

    const isRecoverable = [YouTube.PlayerState.PAUSED, YouTube.PlayerState.ENDED, YouTube.PlayerState.UNSTARTED, YouTube.PlayerState.CUED].includes(state);

    if (!isRecoverable) return;

    // General cap across all states prevents infinite retries
    if (generalRetryCountRef.current >= GENERAL_RETRY_MAX) {
      return;
    }

    const isPaused = state === YouTube.PlayerState.PAUSED;

    // Stricter cap for PAUSED — the most likely state to cause rapid loops
    if (isPaused) {
      if (pausedRetryCountRef.current >= PAUSED_RETRY_MAX) {
        return;
      }

      pausedRetryCountRef.current += 1;
    }

    generalRetryCountRef.current += 1;

    retryTimeout.set(forcePlay, RETRY_DELAY_MS);
  });

  const handleError = useEventCallback((event: YouTubeEvent<number>) => {
    const errorCode = event.data;
    addError(new Error(`YouTube player error: ${YOUTUBE_ERROR_CODES[errorCode] ?? 'unknown'} (code ${errorCode})`), {
      context: 'YoutubeVideoContainer#onError',
      videoId,
      errorCode,
    });
    onError();
  });

  const optionsFromVideoUrl = useMemo(() => {
    const options: Record<string, string> = {};

    if (!videoUrl) {
      return options;
    }

    let searchParams: URLSearchParams | undefined;

    try {
      searchParams = new URL(videoUrl).searchParams;
    } catch (error) {
      addError(error, { context: 'YoutubeVideoContainer#optionsFromVideoUrl', videoUrl });
      return options;
    }

    if (!searchParams) {
      return options;
    }

    searchParams.forEach((value, key) => {
      switch (key.toLowerCase()) {
        case 'v':
          // Skip 'v' param since videoId is already handled elsewhere
          break;
        case 't':
          options.start = value;
          break;
        default:
          options[key] = value;
      }
    });

    return options;
  }, [videoUrl]);

  return (
    <Box
      position="absolute"
      top="50%"
      left="50%"
      transform="translate(-50%, -50%)"
      width="100vw"
      height="100vh"
      minWidth={minWidth}
      minHeight={minHeight}
      zIndex={1}
      opacity={showVideo ? 1 : 0}
      transition="opacity 0.3s ease-in-out"
      __css={{
        '& > div': { width: '100%', height: '100%' },
        '& iframe': { width: '100%', height: '100%' },
      }}
    >
      <YouTube
        videoId={videoId}
        title={tq`Video Background`}
        opts={{
          playerVars: {
            ...optionsFromVideoUrl,
            // Defaults for background video behavior
            autoplay: 1,
            mute: 1,
            controls: 0,
            loop: 1,
            playlist: videoId,
            playsinline: 1,
            modestbranding: 1,
            rel: 0,
            disablekb: 1,
            fs: 0,
          },
        }}
        onReady={handleReady}
        onStateChange={handleStateChange}
        onError={handleError}
      />
    </Box>
  );
};
